import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TokenRefreshError } from '@app-core/models/errors.model';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { DigitaloceanOauthTokenRes } from 'cms-typescript-sdk/digitalocean/models';
import { OauthServiceService } from 'cms-typescript-sdk/digitalocean/services';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from 'node_modules/@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from '../providers/token.service';

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {

  constructor(public token: TokenService,
              private authService: OauthServiceService,
              private snackbar: MatSnackBar,
              private router: Router) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const isApiCalls = request.url.indexOf('/api/') > -1;
    const isTokenRequestCall = request.url.indexOf('/oauth/token') > -1;
    // TODO: take care of swagger configuration changes
    const isLoginOrRegisterCall = request.url.indexOf('login/') > -1 || request.url.indexOf('register') > -1;

    const isSecuredCall = isApiCalls && !isTokenRequestCall && !isLoginOrRegisterCall;

    if (isSecuredCall) {

      if (this.token.isTokenExpired) {
        return this.callRefreshToken(request, next);
      } else {
        request = this.setHeaders(request);
      }

    }

    return next.handle(request)
      .pipe(
        catchError(err => {
          if (err instanceof TokenRefreshError) {
            this.sessionExpired();
          }

          if (err instanceof HttpErrorResponse && err.status === 401) {
            if (isSecuredCall) {
              return this.callRefreshToken(request, next);
            } else {
              this.sessionExpired();
            }
          }

          throw err;
        })
      );
  }

  private callRefreshToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authService.OauthToken({
      grant_type: 'refresh_token',
      refresh_token: this.token.authInfo.refresh_token
    }).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err instanceof HttpErrorResponse && [401, 404, 429].includes(err.status)) {
          throw new TokenRefreshError();
        }

        throw err;
      }),
      switchMap((res: DigitaloceanOauthTokenRes) => {
        this.token.authInfo = res;
        request = this.setHeaders(request);
        return next.handle(request);
      })
    );
  }

  private setHeaders(request) {
    if (!this.token.authInfo || !this.token.authInfo.access_token) {
      throw new Error('User access token is not accessible, unable to process request');
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${ this.token.authInfo.access_token }`
      }
    });
  }

  private sessionExpired() {
    const message = _('SESSION_EXPIRED_MESSAGE')
    this.snackbar.open(message)
    this.router.navigate(['/login']);
  }
}
